import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Monitor, Cpu, Shield, Video, Zap, Navigation, Package, Building2, Cog, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import SectionSeparator from '../components/ui/SectionSeparator';
import { services } from '../data/mock';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';

const iconMap = {
  Monitor, Cpu, Shield, Video, Zap, Navigation, Package, Building2, Cog
};

const Services = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen">
      <SEO 
        title="Products & Services | SRAGCircle OPC - IT Systems, SCADA, Security Solutions"
        description="Explore SRAGCircle OPC's comprehensive products and services including IT systems integration, SCADA automation, security surveillance, electrical power solutions, GPS fleet tracking, and facilities management in the Philippines."
        keywords="IT systems integration services, SCADA automation Philippines, security surveillance CCTV, electrical power equipment supplier, GPS fleet tracking, facilities management Metro Manila, audio visual systems"
        canonical="/services"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#0a0a1a] via-[#0d1a2d] to-[#0a0a1a] overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-[#0066CC]/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-[#00C0FF]/10 rounded-full blur-[80px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#00C0FF] font-semibold text-sm tracking-wider uppercase">Products & Services</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-6">
              Integrated <span className="text-[#0066CC]">Solutions</span> for Modern Organizations
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              From IT infrastructure to facilities management, we deliver comprehensive, turnkey solutions tailored to your needs.
            </p>
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#0066CC] font-semibold text-sm tracking-wider uppercase">Service Details</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a5c] mt-3 mb-4">
              Explore Our Capabilities
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Click on each service to see the full range of solutions we offer.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <AccordionItem
                  key={service.id}
                  id={service.slug}
                  value={service.slug}
                  className="bg-gray-50 rounded-2xl border border-gray-100 px-6 overflow-hidden data-[state=open]:bg-gradient-to-br data-[state=open]:from-[#0066CC]/5 data-[state=open]:to-[#00C0FF]/5 data-[state=open]:border-[#0066CC]/30 transition-all duration-300"
                >
                  <AccordionTrigger className="py-6 hover:no-underline">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#0066CC]/10 rounded-xl flex items-center justify-center">
                        {Icon && <Icon className="w-6 h-6 text-[#0066CC]" />}
                      </div>
                      <div className="text-left">
                        <h3 className="text-lg font-semibold text-[#1a1a5c]">
                          {service.title}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {service.shortDescription}
                        </p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <div className="pl-16">
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {service.fullDescription}
                      </p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-3 text-gray-600">
                            <CheckCircle className="w-5 h-5 text-[#0066CC] flex-shrink-0" />
                            <span className="text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </section>

      {/* Turnkey Solutions */}
      <section className="py-20 bg-[#0a0a1a] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#0066CC]/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-1/4 w-72 h-72 bg-[#00C0FF]/5 rounded-full blur-[80px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00C0FF] font-semibold text-sm tracking-wider uppercase">End-to-End Solutions</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">
              Turnkey Project Capabilities
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              We deliver complete solutions from concept to completion
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Consultancy & Design",
                description: "Expert consultation and custom system design tailored to your specific requirements and industry standards."
              },
              {
                step: "02",
                title: "Supply & Installation",
                description: "Professional procurement, delivery, and installation of quality-assured equipment and systems."
              },
              {
                step: "03",
                title: "Maintenance & Support",
                description: "Long-term maintenance, support, and lifecycle management to ensure optimal performance."
              }
            ].map((item, index) => (
              <div key={index} className="glass rounded-2xl p-8 text-center">
                <div className="text-5xl font-bold text-[#00C0FF] mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#0066CC] to-[#004499]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Need a Custom Solution?
          </h2>
          <p className="text-xl text-white/80 mb-10">
            Our team can design and implement integrated solutions tailored to your organization's unique requirements.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#0066CC] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-xl">
              Request a Proposal
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
